import React from 'react'
import {
  Modal,
  Button,
  Card,
  Row,
  Col,
  Spinner,
  Alert,
  ProgressBar,
  Badge,
  Table,
} from 'react-bootstrap'
import { Ansible } from 'interfaces/ansible'
import { PnsMacMini } from 'interfaces/pns-mac-mini'
import useAvailableAnsibles from 'hooks/use-available-ansibles'
import useMacMiniAssignmentWithNaming, {
  extractTerminalPrefix,
  extractAnsibleNumber,
  findNextMacMiniNumber,
  generateMacMiniName,
} from 'hooks/use-mac-mini-assignment-with-naming'
import useMacMinisByAnsible from 'hooks/use-mac-minis-by-ansible'

interface MacMiniAssignmentModalProps {
  show: boolean
  onHide: () => void
  macMinis: PnsMacMini[]
  terminalId: string
  terminalName?: string
  onSuccess?: () => void
}

const MacMiniAssignmentModal: React.FC<MacMiniAssignmentModalProps> = ({
  show,
  onHide,
  macMinis,
  terminalId,
  terminalName,
  onSuccess,
}) => {
  const macMiniCount = macMinis.length

  const {
    availableAnsibles,
    isLoading: ansiblesLoading,
    error: ansiblesError,
  } = useAvailableAnsibles({
    terminalId,
    requiredMacMinis: macMiniCount,
    requiredPhones: 0, // Only Mac Minis for this flow
    enabled: show && macMinis.length > 0,
  })

  const { assignMacMinisToAnsible, isAssigning, assignmentProgress } =
    useMacMiniAssignmentWithNaming()

  const handleAnsibleSelect = async (ansible: Ansible) => {
    try {
      await assignMacMinisToAnsible(macMinis, ansible, terminalId)
      onSuccess?.()
      onHide()
    } catch (error) {
      // Error is already handled in the hook
    }
  }

  const getCapacityInfo = (ansible: Ansible) => {
    const availableMacMinis = ansible.maxMacMinis - ansible.currentMacMinis

    return {
      availableMacMinis,
      totalAvailable: availableMacMinis,
    }
  }

  const getCapacityVariant = (used: number, max: number) => {
    const percentage = (used / max) * 100
    if (percentage < 70) return 'success'
    if (percentage < 90) return 'warning'
    return 'danger'
  }

  // Function to get the next Mac Mini name for an ansible
  const getNextMacMiniName = (ansible: Ansible): string => {
    // Get existing Mac Minis for this ansible (we'll use the data from the ansible object)
    // Since we don't have the actual Mac Mini list here, we'll calculate based on current count
    const terminalName = ansible.terminal?.name || 'T000'
    const nextNumber = ansible.currentMacMinis + 1

    return generateMacMiniName(terminalName, ansible.name, nextNumber)
  }

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          Assign {macMinis.length} Mac Mini{macMinis.length === 1 ? '' : 's'} to
          Ansible
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Assignment Progress */}
        {isAssigning && assignmentProgress && (
          <Alert variant="info" className="mb-3">
            <div className="d-flex align-items-center">
              <Spinner animation="border" size="sm" className="me-2" />
              <div className="flex-grow-1">
                <div>{assignmentProgress.currentAction}</div>
                <ProgressBar
                  now={
                    (assignmentProgress.current / assignmentProgress.total) *
                    100
                  }
                  className="mt-2"
                  variant="info"
                />
                {assignmentProgress.previewNames && (
                  <div className="mt-2">
                    <small className="text-muted">Name changes:</small>
                    {assignmentProgress.previewNames
                      .slice(0, 3)
                      .map((nameChange, index) => (
                        <div key={index} className="small">
                          {nameChange}
                        </div>
                      ))}
                    {assignmentProgress.previewNames.length > 3 && (
                      <div className="small text-muted">
                        ... and {assignmentProgress.previewNames.length - 3}{' '}
                        more
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </Alert>
        )}

        {/* Assignment Summary */}
        <Card className="mb-3">
          <Card.Body>
            <h6>Assignment Summary</h6>
            <p className="mb-2">
              <strong>Terminal:</strong> {terminalName || 'Unknown'}
            </p>
            <p className="mb-2">
              <strong>Mac Minis to assign:</strong>
              <Badge variant="primary" className="ms-2">
                {macMiniCount} Mac Mini{macMiniCount === 1 ? '' : 's'}
              </Badge>
            </p>

            {/* Preview of Mac Minis */}
            {macMinis.length <= 5 ? (
              <div className="mt-2">
                <small className="text-muted">Mac Minis:</small>
                {macMinis.map((macMini, index) => (
                  <div key={macMini.id} className="small">
                    {index + 1}. {macMini.name}
                  </div>
                ))}
              </div>
            ) : (
              <div className="mt-2">
                <small className="text-muted">
                  Mac Minis: {macMinis[0].name}, {macMinis[1].name}, ... and{' '}
                  {macMinis.length - 2} more
                </small>
              </div>
            )}

            <Alert variant="warning" className="mt-3 mb-0">
              <small>
                <strong>Note:</strong> Mac Minis will be renamed with pattern
                T000A{'{XX}'}M{'{XX}'} and status will change to PROVISIONING.
              </small>
            </Alert>
          </Card.Body>
        </Card>

        {/* Available Ansibles */}
        <h6>Select an Available Ansible:</h6>

        {ansiblesLoading ? (
          <div className="text-center py-4">
            <Spinner animation="border" />
            <div className="mt-2">Loading available ansibles...</div>
          </div>
        ) : ansiblesError ? (
          <Alert variant="danger">
            Error loading ansibles: {ansiblesError.message}
          </Alert>
        ) : availableAnsibles.length === 0 ? (
          <Alert variant="warning">
            <h6>No Available Ansibles</h6>
            <p className="mb-0">
              There are no ansibles in terminal <strong>{terminalName}</strong>{' '}
              with sufficient capacity to handle {macMiniCount} Mac Mini
              {macMiniCount === 1 ? '' : 's'}.
            </p>
          </Alert>
        ) : (
          <Row>
            {availableAnsibles.map((ansible) => {
              const capacity = getCapacityInfo(ansible)

              return (
                <Col md={6} key={ansible.id} className="mb-3">
                  <Card
                    className={`h-100 ${isAssigning ? '' : 'cursor-pointer'}`}
                    style={{
                      cursor: isAssigning ? 'not-allowed' : 'pointer',
                      opacity: isAssigning ? 0.6 : 1,
                    }}
                    onClick={() => !isAssigning && handleAnsibleSelect(ansible)}
                  >
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <h6 className="mb-0">{ansible.name}</h6>
                        <span
                          className={`badge bg-${
                            ansible.status === 'ACTIVE' ? 'success' : 'warning'
                          }`}
                        >
                          {ansible.status}
                        </span>
                      </div>

                      {/* Mac Mini Capacity */}
                      <div className="mb-2">
                        <small className="text-muted">Mac Mini Capacity:</small>
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="small">
                            {ansible.currentMacMinis}/{ansible.maxMacMinis} used
                          </span>
                          <span className="small text-success">
                            {capacity.availableMacMinis} available
                          </span>
                        </div>
                        <ProgressBar
                          now={
                            (ansible.currentMacMinis / ansible.maxMacMinis) *
                            100
                          }
                          variant={getCapacityVariant(
                            ansible.currentMacMinis,
                            ansible.maxMacMinis
                          )}
                          style={{ height: '6px' }}
                        />
                      </div>

                      {/* Naming Preview */}
                      <div className="small text-info">
                        <strong>Naming Preview:</strong>
                        <br />
                        Next Mac Mini: {getNextMacMiniName(ansible)}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              )
            })}
          </Row>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isAssigning}>
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default MacMiniAssignmentModal
